import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  Text,
  Title,
  Badge,
  Group,
  Stack,
  Progress,
  Alert,
  ActionIcon,
  Tooltip,
  Select,
  Button,
  NumberInput,
  Switch,
  Divider,
  Center,
  Loader,
  Table,
  ScrollArea
} from '@mantine/core';
import {
  IconDashboard,
  IconAlertTriangle,
  IconRefresh,
  IconDownload,
  IconSettings,
  IconClock,
  IconTrendingUp,
  IconTrendingDown,
  IconActivity,
  IconDatabase,
  IconShield,
  IconBell,
  IconReportAnalytics
} from '@tabler/icons-react';
import { useQuery, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import { useAuditLogger } from '../hooks/useAuditLogger';

// FERP's 16 primitive workflow types
const PRIMITIVE_TYPES = [
  { id: 'wfl', name: 'Workflow', icon: IconActivity, color: 'blue' },
  { id: 'ins', name: 'Integration', icon: IconDatabase, color: 'cyan' },
  { id: 'sig', name: 'Signature', icon: IconShield, color: 'green' },
  { id: 'exe', name: 'Execution', icon: IconActivity, color: 'orange' },
  { id: 'mon', name: 'Monitoring', icon: IconDashboard, color: 'purple' },
  { id: 'gov', name: 'Governance', icon: IconShield, color: 'red' },
  { id: 'pol', name: 'Policy', icon: IconShield, color: 'yellow' },
  { id: 'esc', name: 'Escalation', icon: IconAlertTriangle, color: 'red' },
  { id: 'not', name: 'Notification', icon: IconBell, color: 'pink' },
  { id: 'met', name: 'Metadata', icon: IconDatabase, color: 'gray' },
  { id: 'aud', name: 'Audit', icon: IconShield, color: 'dark' },
  { id: 'rep', name: 'Reporting', icon: IconReportAnalytics, color: 'indigo' },
  { id: 'ana', name: 'Analytics', icon: IconTrendingUp, color: 'teal' },
  { id: 'opt', name: 'Optimization', icon: IconTrendingUp, color: 'lime' },
  { id: 'rel', name: 'Release', icon: IconActivity, color: 'violet' },
  { id: 'lif', name: 'Lifecycle', icon: IconClock, color: 'grape' }
] as const;

// GraphQL queries for primitive metrics
const GET_PRIMITIVE_METRICS = gql`
  query GetPrimitiveMetrics($tenantId: String!, $timeWindow: String!) {
    sagaPrimitiveMetrics(tenantId: $tenantId, timeWindow: $timeWindow) {
      primitiveType
      executionCount
      averageLatencyMs
      successRate
      errorCount
      compensationRate
      lastExecutionTime
      performanceTrend
      criticalAlerts {
        alertType
        message
        severity
        timestamp
      }
    }
  }
`;

const SUBSCRIBE_PRIMITIVE_ALERTS = gql`
  subscription SubscribePrimitiveAlerts($tenantId: String!, $primitiveTypes: [String!]) {
    sagaStateTransitions(tenant: $tenantId, primitiveTypes: $primitiveTypes) {
      eventId
      tenant
      sagaExecutionId
      eventType
      primitiveType
      fromState
      toState
      stepId
      executionResult
      auditTrailId
      timestamp
      userId
      safetyScore
      performanceMetrics
    }
  }
`;

interface PrimitiveMetric {
  primitiveType: string;
  executionCount: number;
  averageLatencyMs: number;
  successRate: number;
  errorCount: number;
  compensationRate: number;
  lastExecutionTime: string;
  performanceTrend: 'UP' | 'DOWN' | 'STABLE';
  criticalAlerts: Array<{
    alertType: string;
    message: string;
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    timestamp: string;
  }>;
}

interface PrimitiveAlert {
  primitiveType: string;
  alertType: string;
  message: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  timestamp: string;
  executionId: string;
  metadata: Record<string, any>;
}

export const SagaPrimitiveMetricsDashboard: React.FC = () => {
  const { currentTenant } = useCurrentTenant();
  const { logUserAction } = useAuditLogger();

  // State management
  const [timeWindow, setTimeWindow] = useState('1h');
  const [selectedPrimitive, setSelectedPrimitive] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [alerts, setAlerts] = useState<PrimitiveAlert[]>([]);

  // GraphQL queries
  const { data: metricsData, loading, error, refetch } = useQuery<Record<string, any>>(GET_PRIMITIVE_METRICS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      timeWindow
    },
    pollInterval: autoRefresh ? refreshInterval * 1000 : 0,
    skip: !currentTenant?.tenantId
  });

  // Real-time alert subscription (mapped from state transitions)
  useSubscription<Record<string, any>>(SUBSCRIBE_PRIMITIVE_ALERTS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      primitiveTypes: undefined // Listen to all primitive types
    },
    skip: !currentTenant?.tenantId || !alertsEnabled,
    onData: ({ data }) => {
      if (data.data?.sagaStateTransitions) {
        const event = data.data?.sagaStateTransitions;

        // Convert state transition to alert format (filter for failures/errors)
        if (event.toState === 'failed' || event.toState === 'error' || event.toState === 'compensated') {
          const alert: PrimitiveAlert = {
            primitiveType: event.primitiveType,
            alertType: event.toState === 'failed' ? 'EXECUTION_FAILED' : event.toState === 'error' ? 'EXECUTION_ERROR' : 'COMPENSATED',
            message: `${event.eventType} transition to ${event.toState}`,
            severity: event.toState === 'failed' ? 'CRITICAL' : event.toState === 'error' ? 'HIGH' : 'MEDIUM',
            timestamp: event.timestamp,
            executionId: event.sagaExecutionId,
            metadata: { eventId: event.eventId, fromState: event.fromState, toState: event.toState, stepId: event.stepId }
          };

          setAlerts(prev => [alert, ...prev.slice(0, 49)]); // Keep last 50 alerts

          // Show notification for critical alerts
          if (alert.severity === 'CRITICAL' || alert.severity === 'HIGH') {
            notifications.show({
              title: `${alert.primitiveType.toUpperCase()} Alert`,
              message: alert.message,
              color: alert.severity === 'CRITICAL' ? 'red' : 'orange',
              icon: <IconAlertTriangle size={20} />,
              autoClose: false
            });
          }

          // Log alert for audit trail
          logUserAction({
            action: 'saga_primitive_alert_received',
            details: {
              primitiveType: alert.primitiveType,
              alertType: alert.alertType,
              severity: alert.severity,
              executionId: alert.executionId
            },
            metadata: alert.metadata
          });
        }
      }
    }
  });

  // Process metrics data
  const primitiveMetrics = useMemo(() => {
    if (!metricsData?.sagaPrimitiveMetrics) return [];
    return metricsData.sagaPrimitiveMetrics as PrimitiveMetric[];
  }, [metricsData]);

  // Calculate overall system health
  const systemHealth = useMemo(() => {
    if (primitiveMetrics.length === 0) return { score: 0, status: 'UNKNOWN' };

    const avgSuccessRate = primitiveMetrics.reduce((sum, m) => sum + m.successRate, 0) / primitiveMetrics.length;
    const criticalAlertCount = primitiveMetrics.reduce((sum, m) =>
      sum + m.criticalAlerts.filter(a => a.severity === 'CRITICAL').length, 0
    );

    let score = avgSuccessRate;
    if (criticalAlertCount > 0) score *= 0.5; // Critical alerts severely impact health

    let status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'UNKNOWN';
    if (score >= 95) status = 'HEALTHY';
    else if (score >= 85) status = 'WARNING';
    else status = 'CRITICAL';

    return { score, status };
  }, [primitiveMetrics]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    try {
      await refetch();
      logUserAction({
        action: 'saga_metrics_manual_refresh',
        details: { timeWindow }
      });
      notifications.show({
        title: 'Metrics Refreshed',
        message: 'SAGA primitive metrics updated successfully',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Refresh Failed',
        message: 'Failed to refresh metrics data',
        color: 'red'
      });
    }
  }, [refetch, logUserAction, timeWindow]);

  // Handle metrics export
  const handleExportMetrics = useCallback(async () => {
    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        tenantId: currentTenant?.tenantId,
        timeWindow,
        systemHealth,
        primitiveMetrics,
        alerts: alerts.slice(0, 10) // Include recent alerts
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `saga-metrics-${currentTenant?.tenantId}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      logUserAction({
        action: 'saga_metrics_exported',
        details: {
          timeWindow,
          primitiveCount: primitiveMetrics.length,
          alertCount: alerts.length
        }
      });

      notifications.show({
        title: 'Export Complete',
        message: 'Metrics data exported successfully',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export metrics data',
        color: 'red'
      });
    }
  }, [currentTenant?.tenantId, timeWindow, systemHealth, primitiveMetrics, alerts, logUserAction]);

  if (loading) {
    return (
      <Center h={400}>
        <Stack ta="center">
          <Loader size="lg" />
          <Text>Loading SAGA primitive metrics...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error Loading Metrics"
        color="red"
        variant="outline"
      >
        Failed to load SAGA primitive metrics: {error.message}
      </Alert>
    );
  }

  return (
    <Container size="xl" p="md">
      <Stack gap="lg">
        {/* Header Section */}
        <Group justify="space-between" ta="center">
          <div>
            <Title order={2}>SAGA Primitive Metrics Dashboard</Title>
            <Text size="sm" c="dimmed">
              Real-time performance monitoring for FERP&apos;s 16 primitive workflow types
            </Text>
          </div>

          <Group gap="sm">
            <Select
              value={timeWindow}
              onChange={(value: string | null) => setTimeWindow(value || '1h')}
              data={[
                { value: '15m', label: 'Last 15 minutes' },
                { value: '1h', label: 'Last hour' },
                { value: '6h', label: 'Last 6 hours' },
                { value: '24h', label: 'Last 24 hours' },
                { value: '7d', label: 'Last 7 days' }
              ]}
              size="sm"
            />

            <Tooltip label="Refresh metrics">
              <ActionIcon onClick={handleRefresh} variant="outline">
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Export metrics">
              <ActionIcon onClick={handleExportMetrics} variant="outline">
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* System Health Overview */}
        <Card withBorder p="lg">
          <Group justify="space-between" ta="center" mb="md">
            <Title order={3}>System Health Overview</Title>
            <Group gap="sm">
              <Switch
                label="Auto Refresh"
                checked={autoRefresh}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAutoRefresh(event.currentTarget.checked)}
                size="sm"
              />
              <Switch
                label="Real-time Alerts"
                checked={alertsEnabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAlertsEnabled(event.currentTarget.checked)}
                size="sm"
              />
            </Group>
          </Group>

          <Grid>
            <Grid.Col span={3}>
              <Center>
                <Stack ta="center" gap="xs">
                  <Text size="xl" fw={700} c={
                    systemHealth.status === 'HEALTHY' ? 'green' :
                    systemHealth.status === 'WARNING' ? 'orange' : 'red'
                  }>
                    {systemHealth.score.toFixed(1)}%
                  </Text>
                  <Badge
                    color={
                      systemHealth.status === 'HEALTHY' ? 'green' :
                      systemHealth.status === 'WARNING' ? 'orange' : 'red'
                    }
                    variant="filled"
                  >
                    {systemHealth.status}
                  </Badge>
                  <Text size="sm" c="dimmed">Overall Health</Text>
                </Stack>
              </Center>
            </Grid.Col>

            <Grid.Col span={9}>
              <Grid>
                <Grid.Col span={3}>
                  <Text size="sm" c="dimmed">Active Primitives</Text>
                  <Text size="lg" fw={600}>
                    {primitiveMetrics.filter(m => m.executionCount > 0).length}/{PRIMITIVE_TYPES.length}
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" c="dimmed">Total Executions</Text>
                  <Text size="lg" fw={600}>
                    {primitiveMetrics.reduce((sum, m) => sum + m.executionCount, 0).toLocaleString()}
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" c="dimmed">Avg Success Rate</Text>
                  <Text size="lg" fw={600} c="green">
                    {primitiveMetrics.length > 0
                      ? (primitiveMetrics.reduce((sum, m) => sum + m.successRate, 0) / primitiveMetrics.length).toFixed(1)
                      : 0
                    }%
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" c="dimmed">Critical Alerts</Text>
                  <Text size="lg" fw={600} c="red">
                    {alerts.filter(a => a.severity === 'CRITICAL').length}
                  </Text>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Primitive Performance Grid */}
        <Title order={3}>Primitive Performance Metrics</Title>
        <Grid>
          {PRIMITIVE_TYPES.map(primitiveType => {
            const metric = primitiveMetrics.find(m => m.primitiveType === primitiveType.id);
            const Icon = primitiveType.icon;

            return (
              <Grid.Col key={primitiveType.id} span={3}>
                <Card
                  withBorder
                  p="md"
                  style={{
                    cursor: 'pointer',
                    borderColor: selectedPrimitive === primitiveType.id ? 'var(--mantine-color-blue-4)' : undefined
                  }}
                  onClick={() => setSelectedPrimitive(
                    selectedPrimitive === primitiveType.id ? null : primitiveType.id
                  )}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" ta="center">
                      <Group gap="xs">
                        <Icon size={20} style={{ color: `var(--mantine-color-${primitiveType.color}-6)` }} />
                        <Text size="sm" fw={600}>{primitiveType.name}</Text>
                      </Group>

                      {metric?.performanceTrend && (
                        <Tooltip label={`Performance trend: ${metric.performanceTrend}`}>
                          {metric.performanceTrend === 'UP' ? (
                            <IconTrendingUp size={14} style={{ color: 'var(--mantine-color-green-6)' }} />
                          ) : metric.performanceTrend === 'DOWN' ? (
                            <IconTrendingDown size={14} style={{ color: 'var(--mantine-color-red-6)' }} />
                          ) : (
                            <div style={{ width: 14, height: 14, backgroundColor: 'var(--mantine-color-gray-4)', borderRadius: 2 }} />
                          )}
                        </Tooltip>
                      )}
                    </Group>

                    {metric ? (
                      <>
                        <Group justify="space-between">
                          <Text size="xs" c="dimmed">Executions</Text>
                          <Text size="xs" fw={600}>{metric.executionCount.toLocaleString()}</Text>
                        </Group>

                        <Group justify="space-between">
                          <Text size="xs" c="dimmed">Avg Latency</Text>
                          <Text size="xs" fw={600}>{metric.averageLatencyMs}ms</Text>
                        </Group>

                        <div>
                          <Group justify="space-between" mb={2}>
                            <Text size="xs" c="dimmed">Success Rate</Text>
                            <Text size="xs" fw={600}>{metric.successRate.toFixed(1)}%</Text>
                          </Group>
                          <Progress
                            value={metric.successRate}
                            size="xs"
                            color={metric.successRate >= 95 ? 'green' : metric.successRate >= 85 ? 'orange' : 'red'}
                          />
                        </div>

                        {metric.criticalAlerts.length > 0 && (
                          <Alert color="red" icon={<IconAlertTriangle size={12} />}>
                            {metric.criticalAlerts.length} critical alert{metric.criticalAlerts.length > 1 ? 's' : ''}
                          </Alert>
                        )}
                      </>
                    ) : (
                      <Text size="xs" c="dimmed" style={{ textAlign: 'center', padding: '20px 0' }}>
                        No executions in selected timeframe
                      </Text>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>
            );
          })}
        </Grid>

        {/* Detailed Primitive View */}
        {selectedPrimitive && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>
                  {PRIMITIVE_TYPES.find(p => p.id === selectedPrimitive)?.name} Primitive Details
                </Title>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setSelectedPrimitive(null)}
                >
                  Close Details
                </Button>
              </Group>

              {(() => {
                const metric = primitiveMetrics.find(m => m.primitiveType === selectedPrimitive);
                if (!metric) {
                  return (
                    <Text c="dimmed">No data available for this primitive in the selected timeframe.</Text>
                  );
                }

                return (
                  <Grid>
                    <Grid.Col span={6}>
                      <Stack gap="sm">
                        <Text size="sm" fw={600}>Performance Metrics</Text>
                        <Group justify="space-between">
                          <Text size="sm">Total Executions:</Text>
                          <Text size="sm" fw={600}>{metric.executionCount.toLocaleString()}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm">Average Latency:</Text>
                          <Text size="sm" fw={600}>{metric.averageLatencyMs}ms</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm">Success Rate:</Text>
                          <Text size="sm" fw={600} c="green">{metric.successRate.toFixed(2)}%</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm">Error Count:</Text>
                          <Text size="sm" fw={600} c="red">{metric.errorCount}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm">Compensation Rate:</Text>
                          <Text size="sm" fw={600} c="orange">{metric.compensationRate.toFixed(2)}%</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm">Last Execution:</Text>
                          <Text size="sm">{new Date(metric.lastExecutionTime).toLocaleString()}</Text>
                        </Group>
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={6}>
                      {metric.criticalAlerts.length > 0 && (
                        <Stack gap="sm">
                          <Text size="sm" fw={600}>Recent Alerts</Text>
                          <ScrollArea h={200}>
                            <Stack gap="xs">
                              {metric.criticalAlerts.map((alert, index) => (
                                <Alert
                                  key={index}
                                  color={
                                    alert.severity === 'CRITICAL' ? 'red' :
                                    alert.severity === 'HIGH' ? 'orange' :
                                    alert.severity === 'MEDIUM' ? 'yellow' : 'blue'
                                  }
                                  icon={<IconAlertTriangle size={14} />}
                                >
                                  <Stack gap={2}>
                                    <Text size="xs" fw={600}>{alert.alertType}</Text>
                                    <Text size="xs">{alert.message}</Text>
                                    <Text size="xs" c="dimmed">
                                      {new Date(alert.timestamp).toLocaleString()}
                                    </Text>
                                  </Stack>
                                </Alert>
                              ))}
                            </Stack>
                          </ScrollArea>
                        </Stack>
                      )}
                    </Grid.Col>
                  </Grid>
                );
              })()}
            </Stack>
          </Card>
        )}

        {/* Recent Alerts Table */}
        {alerts.length > 0 && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>Recent Alerts</Title>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setAlerts([])}
                >
                  Clear All
                </Button>
              </Group>

              <ScrollArea h={300}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Timestamp</Table.Th>
                      <Table.Th>Primitive</Table.Th>
                      <Table.Th>Severity</Table.Th>
                      <Table.Th>Alert Type</Table.Th>
                      <Table.Th>Message</Table.Th>
                      <Table.Th>Execution ID</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {alerts.slice(0, 20).map((alert, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Text size="xs">{new Date(alert.timestamp).toLocaleString()}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={
                            PRIMITIVE_TYPES.find(p => p.id === alert.primitiveType)?.color || 'gray'
                          }>
                            {alert.primitiveType.toUpperCase()}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            color={
                              alert.severity === 'CRITICAL' ? 'red' :
                              alert.severity === 'HIGH' ? 'orange' :
                              alert.severity === 'MEDIUM' ? 'yellow' : 'blue'
                            }
                          >
                            {alert.severity}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{alert.alertType}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" style={{ maxWidth: 300 }} truncate>
                            {alert.message}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace">{alert.executionId.slice(0, 8)}...</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Card>
        )}

        {/* Configuration Panel */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>Dashboard Configuration</Title>
              <ActionIcon variant="outline">
                <IconSettings size={16} />
              </ActionIcon>
            </Group>

            <Grid>
              <Grid.Col span={4}>
                <NumberInput
                  label="Refresh Interval (seconds)"
                  value={refreshInterval}
                  onChange={(value: string | number) => setRefreshInterval(Number(value) || 30)}
                  min={5}
                  max={300}
                  disabled={!autoRefresh}
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <Switch
                  label="Enable Performance Trending"
                  size="md"
                  mt="xl"
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <Switch
                  label="Show Compensation Metrics"
                  size="md"
                  mt="xl"
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};